import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { map } from '@/api/driver'
import { watches as watchApi } from '@/api/proposed'
import type { MapStation } from '@/api/types'
import { useAuth } from './auth'
import { connectorState } from './charging'
import { useLocal } from './local'
import { personalStore } from './personal'
import { session } from './session'

export interface LocalWatch {
  station_id: number
  station_name: string
  connector_type: string | null
  until: number
  server_id?: number
}

const store = personalStore<LocalWatch[]>('watches', [])
export const WATCH_MINUTES = 120

export function freeMatching(s: MapStation, type: string | null) {
  return s.chargers.flatMap((c) => c.connectors.filter((k) => connectorState(c, k).startable && (!type || k.connector_type === type)).map((k) => ({ c, k })))
}

function beep() {
  try {
    const ctx = new AudioContext()
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.frequency.value = 880
    g.gain.setValueAtTime(0.2, ctx.currentTime)
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6)
    o.connect(g).connect(ctx.destination)
    o.start()
    o.stop(ctx.currentTime + 0.6)
  } catch {
    /* no audio */
  }
}

export function useWatches() {
  const { user } = useAuth()
  // Expired watches are cleared by WatchRunner, so the list can be read as is.
  const list = useLocal(store)
  const add = async (s: MapStation, type: string | null) => {
    const revision = session.revision()
    if ('Notification' in window && Notification.permission === 'default') await Notification.requestPermission().catch(() => undefined)
    const w: LocalWatch = { station_id: s.id, station_name: s.name, connector_type: type, until: Date.now() + WATCH_MINUTES * 60_000 }
    if (revision !== session.revision()) return w
    // Also ask the server (D13) so the phone is notified with the site closed; fine if it doesn't exist yet.
    if (user) {
      try {
        w.server_id = (await watchApi.create({ station_id: s.id, connector_type: type, minutes: WATCH_MINUTES })).id
      } catch {
        /* local only */
      }
    }
    if (revision === session.revision()) store.set([...store.get().filter((x) => x.station_id !== s.id), w])
    return w
  }
  const remove = (stationId: number) => {
    const w = store.get().find((x) => x.station_id === stationId)
    if (w?.server_id) watchApi.remove(w.server_id).catch(() => undefined)
    store.set(store.get().filter((x) => x.station_id !== stationId))
  }
  return { list, add, remove, get: (stationId: number) => list.find((w) => w.station_id === stationId) ?? null }
}

/** Mounted once: while the site is open, checks watched stations every 20 s and alerts when a connector frees up. */
export function WatchRunner() {
  const watches = useLocal(store)
  const busy = useRef(false)
  useEffect(() => {
    if (!watches.length) return
    let cancelled = false
    const revision = session.revision()
    const check = async () => {
      const now = Date.now()
      const live = watches.filter((w) => w.until > now)
      if (live.length < watches.length) {
        store.set(store.get().filter((w) => w.until > now))
        return
      }
      if (busy.current) return
      busy.current = true
      try {
        const res = await map.stations()
        if (cancelled || revision !== session.revision()) return
        const fired: number[] = []
        for (const w of live) {
          const s = res.stations.find((x) => x.id === w.station_id)
          const free = s ? freeMatching(s, w.connector_type) : []
          if (!free.length) continue
          fired.push(w.station_id)
          const title = `${w.connector_type ?? 'A connector'} is free at ${w.station_name}`
          const body = free.map(({ c, k }) => `${c.id} #${k.connector_number}`).join(', ')
          beep()
          navigator.vibrate?.([200, 100, 200])
          if ('Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification(title, { body, tag: `watch-${w.station_id}`, icon: '/icon-512.png' })
            } catch {
              toast.success(title, { description: body, duration: 15_000 })
            }
          } else toast.success(title, { description: body, duration: 15_000 })
        }
        if (fired.length) store.set(store.get().filter((x) => !fired.includes(x.station_id)))
      } catch {
        /* try again next round */
      } finally {
        busy.current = false
      }
    }
    check()
    const id = window.setInterval(check, 20_000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [watches])
  return null
}
